import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, NumberInput, Pill, DataTable, type Column } from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import { QUERY_CREDORES_OPERAVEIS, listarCredoresOperaveis } from "../../../lib/credores-operaveis";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../../lib/unidades-operaveis";
import type { AdminApiToken, ApiAudience, ApiEnvironment, ApiScope, ApiScopeDef } from "@atlas/sdk";

const AUDIENCE_LABEL: Record<ApiAudience, string> = {
  banco: "Consignatária",
  servidor: "Servidor",
  averbadora: "Averbadora",
  prefeitura: "Prefeitura",
};

// Fallback caso o backend (bundle antigo em cache) ainda nao envie scopeCatalog:
// vira um catalogo minimo com os escopos legados read/write/webhooks.
const CATALOG_FALLBACK: Record<ApiAudience, ApiScopeDef[]> = {
  banco: [
    { scope: "banco:read", grupo: "Geral", label: "Leitura (todas as operações de consulta)", tipo: "read" },
    { scope: "banco:write", grupo: "Geral", label: "Escrita (todas as operações de mudança)", tipo: "write" },
    { scope: "banco:webhooks", grupo: "Webhooks", label: "Gerenciar webhooks", tipo: "webhooks" },
  ],
  servidor: [
    { scope: "servidor:read", grupo: "Geral", label: "Leitura (todas as operações de consulta)", tipo: "read" },
    { scope: "servidor:write", grupo: "Geral", label: "Escrita (todas as operações de mudança)", tipo: "write" },
  ],
  averbadora: [
    { scope: "averbadora:read", grupo: "Geral", label: "Leitura (todas as operações de consulta)", tipo: "read" },
    { scope: "averbadora:write", grupo: "Geral", label: "Escrita (todas as operações de mudança)", tipo: "write" },
    { scope: "averbadora:webhooks", grupo: "Webhooks", label: "Webhooks e eventos", tipo: "webhooks" },
  ],
  prefeitura: [
    { scope: "prefeitura:read", grupo: "Geral", label: "Leitura (todas as operações de consulta)", tipo: "read" },
    { scope: "prefeitura:write", grupo: "Geral", label: "Escrita (todas as operações de mudança)", tipo: "write" },
  ],
};

export function AverbadoraApiTokens() {
  const qc = useQueryClient();
  const [env, setEnv] = useState<ApiEnvironment | "all">("all");
  const [aud, setAud] = useState<ApiAudience | "all">("all");
  const [modalOpen, setModalOpen] = useState(false);
  // Plaintexts revelados nesta sessão do navegador (perde no refresh — não há persistência).
  const [revealable, setRevealable] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Token recém-criado — abre modal de revelação imediato.
  const [justCreated, setJustCreated] = useState<{ name: string; plaintext: string } | null>(null);

  const q = useQuery({
    queryKey: ["api-tokens", env, aud],
    queryFn: () => atlas.admin.listApiTokens({
      ...(env === "all" ? {} : { environment: env }),
      ...(aud === "all" ? {} : { audience: aud }),
    }),
  });

  const scopeCatalog = q.data?.scopeCatalog ?? CATALOG_FALLBACK;

  const rows = q.data?.tokens ?? [];

  // Token que o usuário pediu para pausar — segura o aviso antes de confirmar.
  const [confirmPause, setConfirmPause] = useState<AdminApiToken | null>(null);
  const pause = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => atlas.admin.pauseApiToken(id, paused),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });

  const cols: Column<AdminApiToken>[] = [
    { key: "name", header: "Nome", render: (t) => <b>{t.name}</b> },
    { key: "audience", header: "Camada", render: (t) => <Pill variant="aceita">{AUDIENCE_LABEL[t.audience] ?? t.audience}</Pill> },
    // Rotulo em portugues, igual ao seletor de servidor da documentacao — la o
    // banco le "Homologação", e ver "sandbox" aqui obrigava a traduzir de cabeca.
    { key: "env", header: "Ambiente", render: (t) => <Pill variant={t.environment === "production" ? "averbado" : "pendente"}>{t.environment === "production" ? "Produção" : "Homologação"}</Pill> },
    { key: "partner", header: "ID", render: (t) => (t.audience === "averbadora" ? "—" : `#${t.partnerId}`) },
    {
      key: "prefix",
      header: "Chave",
      render: (t) => {
        // Homologacao devolve a chave inteira (o plaintext e' persistido de
        // proposito, pra averbadora e banco reaverem). Producao so tem o
        // prefixo — o plaintext nao existe em lugar nenhum depois da emissao.
        // `revealable` cobre o recem-criado de producao, visivel so nesta aba.
        const plaintext = t.token ?? revealable[t.id] ?? null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <code
              title={plaintext ? "Chave completa (homologação)" : "Só o prefixo — a chave de produção não é recuperável"}
              style={{
                fontFamily: "var(--font-mono)", fontSize: 12,
                color: plaintext ? "var(--text)" : "var(--text-muted)",
                maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                display: "inline-block", verticalAlign: "middle",
              }}
            >
              {plaintext ?? t.prefix}
            </code>
            {plaintext ? (
              <button
                type="button"
                title={t.token ? "Copiar chave" : "Copiar token (disponível apenas nesta sessão)"}
                onClick={async () => {
                  await navigator.clipboard.writeText(plaintext);
                  setCopiedId(t.id);
                  setTimeout(() => setCopiedId((curr) => (curr === t.id ? null : curr)), 1500);
                }}
                style={{
                  border: "1px solid var(--border-strong)",
                  background: "transparent",
                  color: copiedId === t.id ? "var(--emerald-500)" : "var(--accent)",
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {copiedId === t.id ? "✓" : "⧉"}
              </button>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "scopes",
      header: "Permissões",
      render: (t) => (
        // Legado read/write cobre a camada inteira — mostra "todas" em vez de "1".
        <span title={t.scopes.join("\n")} style={{ fontSize: 11, color: "var(--text-muted)", cursor: "help" }}>
          {t.scopes.some((s) => s === `${t.audience}:read` || s === `${t.audience}:write`) ? "amplas (legado)" : t.scopes.length}
        </span>
      ),
    },
    {
      key: "lastUsedAt",
      header: "Último uso",
      // LAST_USED_AT_THROTTLE_MS (5 min) em apps/api/src/modules/admin/api-tokens.ts.
      // Só chamadas autenticadas de /v1/external/banco/* carimbam este campo.
      render: (t) => (
        <span title="Atualizado no máximo a cada 5 minutos, nas chamadas autenticadas de /v1/external/banco. Dentro dessa janela o horário não muda.">
          {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString("pt-BR") : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (t) => {
        const manual = !!t.pausedAt;
        const porBanco = !manual && !!t.bancoInativo;
        const label = manual ? "Desativado" : porBanco ? "Desativado (consignatária)" : "Ativo";
        const dica = manual ? "Token desativado manualmente. O perfil/parceria segue ativo." : porBanco ? "Inativo porque a consignatária dona está pausada. Volta ao reativá-la." : undefined;
        return (
          <span title={dica}>
            <Pill variant={manual || porBanco ? "expirado" : "emdia"}>{label}</Pill>
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (t) => {
        if (t.pausedAt) {
          return (
            <Button size="sm" variant="ghost" disabled={pause.isPending} onClick={() => pause.mutate({ id: t.id, paused: false })}>
              ▶ Reativar token
            </Button>
          );
        }
        if (t.bancoInativo) {
          return <span style={{ fontSize: 11, color: "var(--text-muted)" }}>reative a consignatária</span>;
        }
        return (
          <Button size="sm" variant="ghost" disabled={pause.isPending} onClick={() => setConfirmPause(t)}>
            ⏸ Desativar token
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>API</span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Tokens de acesso</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 4 }}>
            Cada token pertence a uma camada (Consignatária / Servidor / Averbadora / Prefeitura) e consome <code>/v1/external/&lt;camada&gt;/*</code>, com permissões marcadas por operação. Plaintext exibido apenas na criação.
            Você pode desativar um token individualmente sem desativar o perfil/parceria. Nada é apagado: desativar é reversível (dá pra reativar depois). Tokens de uma consignatária pausada também ficam inativos e voltam ao reativá-la.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={aud} onChange={(e) => setAud(e.target.value as typeof aud)} style={selStyle}>
            <option value="all">Todas camadas</option>
            <option value="banco">Consignatária</option>
            <option value="servidor">Servidor</option>
            <option value="averbadora">Averbadora</option>
            <option value="prefeitura">Prefeitura</option>
          </select>
          <select value={env} onChange={(e) => setEnv(e.target.value as typeof env)} style={selStyle}>
            <option value="all">Todos ambientes</option>
            <option value="production">Produção</option>
            <option value="sandbox">Homologação</option>
          </select>
          <Button onClick={() => setModalOpen(true)}>+ Novo token</Button>
        </div>
      </header>

      <Card>
        <DataTable<AdminApiToken> rows={rows} columns={cols} rowKey={(t) => t.id} loading={q.isLoading} emptyState="Nenhum token." />
      </Card>

      {modalOpen ? (
        <TokenModal
          scopeCatalog={scopeCatalog}
          onClose={() => setModalOpen(false)}
          onCreated={(id, name, plaintext) => {
            setRevealable((prev) => ({ ...prev, [id]: plaintext }));
            setJustCreated({ name, plaintext });
            qc.invalidateQueries({ queryKey: ["api-tokens"] });
          }}
        />
      ) : null}

      {justCreated ? (
        <RevealModal token={justCreated} onClose={() => setJustCreated(null)} />
      ) : null}

      {confirmPause ? (
        <PauseWarningModal
          token={confirmPause}
          pending={pause.isPending}
          onCancel={() => setConfirmPause(null)}
          onConfirm={() => pause.mutate({ id: confirmPause.id, paused: true }, { onSuccess: () => setConfirmPause(null) })}
        />
      ) : null}
    </div>
  );
}

function PauseWarningModal({ token, pending, onCancel, onConfirm }: { token: AdminApiToken; pending: boolean; onCancel: () => void; onConfirm: () => void }) {
  const dono = token.audience === "averbadora" ? "a Averbadora"
    : token.audience === "banco" ? `a consignatária #${token.partnerId}`
    : token.audience === "prefeitura" ? `a prefeitura #${token.partnerId}`
    : `o servidor #${token.partnerId}`;
  return (
    <div style={backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>⏸</span>
          <h3 style={{ margin: 0 }}>Desativar o token "{token.name}"?</h3>
        </div>
        <div
          style={{
            marginTop: 14, padding: "12px 14px", borderRadius: 10,
            border: "1px solid color-mix(in srgb, #f59e0b 55%, transparent)",
            background: "color-mix(in srgb, #f59e0b 12%, transparent)",
            color: "var(--text)", fontSize: 13.5, lineHeight: 1.55,
          }}
        >
          Isto <b>desativa apenas este token de acesso</b> — ele para de autenticar na API imediatamente.
          <br />
          <b>{dono} continua ativo</b> e não é afetado: pode operar normalmente e gerar novos tokens. A ação é reversível — você pode reativar o token depois.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onConfirm} disabled={pending}>{pending ? "Desativando…" : "Desativar token"}</Button>
        </div>
      </div>
    </div>
  );
}

function TokenModal({ scopeCatalog, onClose, onCreated }: { scopeCatalog: Record<ApiAudience, ApiScopeDef[]>; onClose: () => void; onCreated: (id: string, name: string, plaintext: string) => void }) {
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState<ApiEnvironment>("sandbox");
  const [audience, setAudience] = useState<ApiAudience>("banco");
  const [partnerId, setPartnerId] = useState<number>(1);
  // Default: só as permissões de LEITURA marcadas — quem cria escolhe cada
  // operação de escrita conscientemente (cliente 07/08/2026).
  const leiturasDe = (aud: ApiAudience) => (scopeCatalog[aud] ?? []).filter((d) => d.tipo === "read").map((d) => d.scope);
  const [scopes, setScopes] = useState<ApiScope[]>(() => leiturasDe("banco"));

  const catalogo = scopeCatalog[audience] ?? [];
  // Agrupamento preservando a ordem do catálogo.
  const grupos = useMemo(() => {
    const out: { grupo: string; defs: ApiScopeDef[] }[] = [];
    for (const def of catalogo) {
      const g = out.find((x) => x.grupo === def.grupo);
      if (g) g.defs.push(def);
      else out.push({ grupo: def.grupo, defs: [def] });
    }
    return out;
  }, [catalogo]);

  // Donos possiveis do token, por camada. Carrega so quando a camada precisa —
  // averbadora nao tem dono e servidor ainda e' por indice numerico.
  const bancosQ = useQuery({
    queryKey: QUERY_CREDORES_OPERAVEIS,
    queryFn: listarCredoresOperaveis,
    enabled: audience === "banco",
    staleTime: 60_000,
  });
  const prefsQ = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
    enabled: audience === "prefeitura",
    staleTime: 60_000,
  });
  const donos = useMemo<{ isLoading: boolean; opcoes: { id: number; nome: string }[] }>(() => {
    if (audience === "banco") {
      return { isLoading: bancosQ.isLoading, opcoes: (bancosQ.data?.bancos ?? []).map((b) => ({ id: b.id, nome: b.nome })) };
    }
    if (audience === "prefeitura") {
      return { isLoading: prefsQ.isLoading, opcoes: (prefsQ.data?.prefeituras ?? []).map((p) => ({ id: p.id, nome: p.nome })) };
    }
    return { isLoading: false, opcoes: [] as { id: number; nome: string }[] };
  }, [audience, bancosQ.isLoading, bancosQ.data, prefsQ.isLoading, prefsQ.data]);

  // Assim que a lista chega, seleciona o primeiro se o id atual nao existe nela.
  // Sem isto o form abriria com partnerId=1 mesmo quando 1 nao e' um id valido
  // (prefeitura 1 pode ter sido removida) e o token nasceria apontando pro nada.
  useEffect(() => {
    if (donos.isLoading || donos.opcoes.length === 0) return;
    if (!donos.opcoes.some((o) => o.id === partnerId)) setPartnerId(donos.opcoes[0]!.id);
  }, [donos, partnerId]);

  function changeAudience(next: ApiAudience) {
    setAudience(next);
    setScopes(leiturasDe(next)); // reset: leituras da camada nova marcadas
    if (next === "averbadora") setPartnerId(0);
    else if (partnerId === 0) setPartnerId(1);
  }

  const create = useMutation({
    mutationFn: () => atlas.admin.createApiToken({ name, environment, audience, partnerId, scopes }),
    onSuccess: (data) => { onCreated(data.token.id, data.token.name, data.plaintext); onClose(); },
  });

  return (
    <div style={backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 12px" }}>Novo token de API</h3>
        <div style={{ ...modalBody, display: "grid", gap: 12, alignContent: "start" }}>
          <Fld lbl="Nome">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Banco Delta — integração" style={inp} />
          </Fld>
          <Fld lbl="Camada (audience)">
            <div style={{ display: "flex", gap: 8 }}>
              {(["banco", "servidor", "averbadora", "prefeitura"] as ApiAudience[]).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => changeAudience(a)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${audience === a ? "var(--accent)" : "var(--border-strong)"}`,
                    background: audience === a ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "transparent",
                    color: "var(--text)", fontWeight: audience === a ? 700 : 500, fontSize: 13,
                  }}
                >
                  {AUDIENCE_LABEL[a]}
                </button>
              ))}
            </div>
          </Fld>
          <div style={{ display: "grid", gridTemplateColumns: audience === "averbadora" ? "1fr" : "1fr 1fr", gap: 12 }}>
            <Fld lbl="Ambiente">
              <select value={environment} onChange={(e) => setEnvironment(e.target.value as ApiEnvironment)} style={selStyle}>
                <option value="sandbox">Homologação (atl_test_*)</option>
                <option value="production">Produção (atl_live_*)</option>
              </select>
            </Fld>
            {audience === "banco" || audience === "prefeitura" ? (
              // Antes era um campo numerico cru: a averbadora tinha que SABER de
              // cabeca o id da prefeitura/banco, e digitar errado gera um token
              // que autentica mas responde 404 em tudo. Agora escolhe pelo nome.
              <Fld lbl={audience === "banco" ? "Consignatária" : "Prefeitura"}>
                <select
                  value={String(partnerId)}
                  onChange={(e) => setPartnerId(Number(e.target.value))}
                  style={selStyle}
                  disabled={donos.isLoading}
                >
                  {donos.isLoading ? <option value="0">Carregando…</option> : null}
                  {!donos.isLoading && donos.opcoes.length === 0
                    ? <option value="0">Nenhum cadastrado</option>
                    : null}
                  {donos.opcoes.map((o) => (
                    <option key={o.id} value={String(o.id)}>#{o.id} — {o.nome}</option>
                  ))}
                </select>
              </Fld>
            ) : audience === "servidor" ? (
              <Fld lbl="ID do servidor">
                <NumberInput value={partnerId} onChange={(e) => setPartnerId(Number(e.target.value))} style={inp} />
              </Fld>
            ) : null}
          </div>
          <Fld lbl={`Permissões da camada ${AUDIENCE_LABEL[audience]} (por operação)`}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "var(--bg-elev-2)", borderRadius: 8, maxHeight: 320, overflowY: "auto" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => setScopes(catalogo.map((d) => d.scope))} style={quickBtn}>Marcar tudo</button>
                <button type="button" onClick={() => setScopes(catalogo.filter((d) => d.tipo === "read").map((d) => d.scope))} style={quickBtn}>Somente leitura</button>
                <button type="button" onClick={() => setScopes([])} style={quickBtn}>Limpar</button>
                <span style={{ fontSize: 11, color: "var(--text-muted)", alignSelf: "center", marginLeft: "auto" }}>
                  {scopes.length} de {catalogo.length} marcadas
                </span>
              </div>
              {grupos.map(({ grupo, defs }) => {
                const todasDoGrupo = defs.every((d) => scopes.includes(d.scope));
                return (
                  <div key={grupo}>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", marginBottom: 4 }}>
                      <input
                        type="checkbox"
                        checked={todasDoGrupo}
                        onChange={(e) => setScopes((arr) => {
                          const fora = arr.filter((x) => !defs.some((d) => d.scope === x));
                          return e.target.checked ? [...fora, ...defs.map((d) => d.scope)] : fora;
                        })}
                      />
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{grupo}</span>
                    </label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingLeft: 22 }}>
                      {defs.map((d) => (
                        <label key={d.scope} style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer", alignItems: "baseline" }}>
                          <input
                            type="checkbox"
                            checked={scopes.includes(d.scope)}
                            onChange={(e) => setScopes((arr) => (e.target.checked ? [...arr, d.scope] : arr.filter((x) => x !== d.scope)))}
                          />
                          <span style={{ flex: 1 }}>
                            {d.label}
                            {d.tipo === "write" ? <Pill variant="pendente">escrita</Pill> : null}
                            <code style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-muted)" }}>{d.scope}</code>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Fld>
        </div>
        {/* Rodape FORA do corpo rolavel: "Criar token" fica sempre alcancavel,
            independente da altura da lista de permissoes. */}
        <div style={{ flexShrink: 0, borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 16 }}>
          {create.isError ? (
            <p style={{ color: "var(--danger-500)", margin: "0 0 10px", fontSize: 13 }}>{(create.error as Error).message}</p>
          ) : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !name || scopes.length === 0}>
              {create.isPending ? "Criando…" : "Criar token"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevealModal({ token, onClose }: { token: { name: string; plaintext: string }; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(token.plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <div style={backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modal, maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, color: "var(--emerald-500)" }}>Token "{token.name}" criado</h3>

        <div
          style={{
            marginTop: 14,
            padding: "12px 14px",
            borderRadius: 10,
            border: "1px solid color-mix(in srgb, #f59e0b 55%, transparent)",
            background: "color-mix(in srgb, #f59e0b 12%, transparent)",
            color: "var(--text)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <b style={{ color: "#d97706" }}>⚠ Copie agora — esta é a única vez que o token será exibido.</b><br />
          Por segurança, o Atlas guarda apenas um hash do token. Depois de fechar esta janela
          <b> não será mais possível copiá-lo</b>. Se perder, exclua e gere um novo.
        </div>

        <pre
          style={{
            marginTop: 14,
            padding: 14,
            background: "var(--bg-elev-2)",
            borderRadius: 10,
            overflow: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            userSelect: "all",
            border: "1px solid var(--border)",
          }}
        >
          {token.plaintext}
        </pre>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <Button onClick={copy}>{copied ? "✓ Copiado!" : "↗ Copiar token"}</Button>
          <Button variant="ghost" onClick={onClose}>Já copiei, fechar</Button>
        </div>
      </div>
    </div>
  );
}

function Fld({ lbl, children }: { lbl: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{lbl}</span>
      {children}
    </label>
  );
}

const inp: React.CSSProperties = { padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text)", fontSize: 14 };
const selStyle: React.CSSProperties = { ...inp, cursor: "pointer" };
const quickBtn: React.CSSProperties = { padding: "4px 10px", borderRadius: 999, border: "1px solid var(--border-strong)", background: "transparent", color: "var(--accent)", fontSize: 11, fontWeight: 600, cursor: "pointer" };
const backdrop: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 100, padding: 24 };

/**
 * Base do modal.
 *
 * Duas correcoes de 24/08/2026, as duas visiveis so com conteudo alto (o form de
 * novo token, com todas as permissoes):
 *
 * 1. FUNDO SOLIDO. Era `var(--surface)`, que e' `rgba(26,41,66,.6)` — 60% de
 *    opacidade. O modal ficava translucido e a tabela atras vazava por baixo do
 *    texto. `--surface-solid` existe pra isto (o modal de reset em
 *    /averbadora/configuracoes ja usava).
 * 2. ALTURA MAXIMA + SCROLL. Sem `maxHeight`, o modal crescia alem da viewport;
 *    como o backdrop centraliza (`placeItems: center`), o excesso saia pelas
 *    DUAS pontas e o rodape com "Criar token" ficava fora da tela, sem barra de
 *    rolagem. So dava pra clicar diminuindo o zoom do navegador.
 *
 * O layout e' coluna flex: quem rola e' o corpo (`modalBody`), entao titulo e
 * rodape ficam sempre visiveis.
 */
const modal: React.CSSProperties = {
  background: "var(--surface-solid, var(--bg-elev-2))",
  borderRadius: 12,
  padding: 24,
  maxWidth: 560,
  width: "100%",
  border: "1px solid var(--border)",
  boxShadow: "var(--shadow-lg)",
  maxHeight: "calc(100vh - 48px)",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};
/** Corpo rolavel. `minHeight: 0` e' o que permite o filho encolher dentro do flex. */
const modalBody: React.CSSProperties = { overflowY: "auto", minHeight: 0, flex: 1, marginRight: -8, paddingRight: 8 };
